import * as jotai from 'jotai'

import * as api from '@owl-nest/api-client/latest'
import * as hooks from '@owl-nest/hooks'
import * as base from './api'
import * as subscription from '../subscription'

const ORDERS_CACHE = jotai.atom<hooks.Cache<api.Order<true>>>({})

type UseOrderParams = {
  context?: string
}

type UseOrderConfig = {
  fetchOnMount?: boolean | 'force'
  params?: UseOrderParams
}

export type UseOrder = {
  response: hooks.Response<api.Order<true>>
  query: () => Promise<void>
  toggleDelivered: () => Promise<void>
  update: (payload: api.UpdateOrder) => Promise<api.ApiResponse<api.Order> | undefined>
  cancel: () => Promise<api.ApiResponse<unknown> | undefined>
  renew: (order: Partial<api.Order>) => Promise<api.ApiResponse<api.Order> | undefined>
}

export function useOrder(orderId?: number, { fetchOnMount = true, params = {} }: UseOrderConfig = {}): UseOrder {
  const sharedQuery = hooks.useSharedQuery(
    async (orderId: number) => base.get(orderId, { getParams: toURLSearchParams(params) }),
    {
      atom: ORDERS_CACHE,
      id,
    },
  )

  hooks.useFetchOnMount({ fetchOnMount, query, getResponse }, id(orderId))

  return { response: getResponse(), query, toggleDelivered, update, cancel, renew }

  function getResponse(): hooks.Response<api.Order<true>> {
    if (orderId === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }
    return sharedQuery.getCurrentResponse([orderId])
  }

  async function query(): Promise<void> {
    if (orderId !== undefined) {
      return sharedQuery.query(orderId)
    }
  }

  async function toggleDelivered(): Promise<void> {
    if (orderId === undefined) {
      return
    }

    await sharedQuery.patch([orderId], async (response) => {
      const beforeData = hooks.data(response)
      const result =
        beforeData?.shipping_status === api.ShippingStatus.DELIVERED
          ? await base.markAsUndelivered(orderId)
          : await base.markAsDelivered(orderId)
      const data = response.data

      if (data === undefined) {
        return undefined
      }

      return result.next((updatedOrder) => {
        return updatedOrder
      })
    })
  }

  async function cancel(): Promise<api.ApiResponse<unknown> | undefined> {
    if (orderId === undefined) {
      return
    }

    return sharedQuery.patch([orderId], async (response) => {
      const data = response.data
      if (data === undefined) {
        throw Error('order must be fetched before calling cancel')
      }

      if (data.type === api.OrderType.MONTHLY) {
        const result = await subscription.cancel(data.subscription.id)
        return result.next((updatedSubscription) => {
          // only the subscription is updated, the rest of the order does not
          // change when cancelling a subscription
          return { ...data, subscription: updatedSubscription }
        })
      } else {
        const result = await base.cancel(orderId)
        return result.next((updatedOrder) => {
          return updatedOrder as api.Order<true>
        })
      }
    })
  }

  async function update(payload: api.UpdateOrder): Promise<api.ApiResponse<api.Order> | undefined> {
    if (orderId === undefined) {
      return
    }

    return await sharedQuery.patch([orderId], async (response) => {
      const result = await base.update(orderId, payload)
      const data = response.data

      if (data === undefined) {
        return undefined
      }

      return result.next((updatedOrder) => {
        return updatedOrder as unknown as api.Order<true>
      })
    })
  }

  async function renew(order: Partial<api.Order>): Promise<api.ApiResponse<api.Order> | undefined> {
    if (orderId === undefined) {
      return
    }

    return sharedQuery.patch([orderId], async () => {
      const result = await base.renew(orderId, order)

      return result.next((updatedOrder) => {
        return updatedOrder as any
      })
    })
  }
}

function id(orderId: number | undefined): string {
  return JSON.stringify([orderId])
}

function toURLSearchParams(params: UseOrderParams = {}, searchParams = new URLSearchParams()): URLSearchParams {
  if (params.context) {
    searchParams.set('context', params.context)
  }

  return searchParams
}
